import React from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { Redirect, useHistory } from 'react-router-dom';

import Box from 'ui-box';
import {
  Button,
  FormField,
  InputGroup,
  majorScale,
  useToast,
} from '../../npaper';
import { useStores } from '../../stores';
import { AuthLayout, AuthTitle } from './AuthLayout';

interface ResetValues {
  password: string;
  confirmPassword: string;
}

export const AuthReset = () => {
  const intl = useIntl();
  const history = useHistory();
  const { authStore } = useStores();
  const toaster = useToast();

  const query = new URLSearchParams(history.location.search);
  const token = query.get('token');

  const {
    register,
    handleSubmit,
    errors,
    formState,
    watch,
    setError,
  } = useForm<ResetValues>();

  const onSubmit = async (values: ResetValues) => {
    try {
      await authStore.resetPassword(values.password, token || '');
      await authStore.setup();
      history.push('/auth/organisations');
      toaster.addToast({
        message: intl.formatMessage({
          defaultMessage: 'New password successfully saved',
        }),
        intent: 'success',
      });
    } catch (error) {
      if (
        error.response &&
        error.response.data &&
        error.response.data.email_token
      ) {
        setError('confirmPassword', {
          message: intl.formatMessage({
            defaultMessage:
              'Reset link has expired. Please request a new reset if you need to reset your password',
          }),
        });
        setError('password', { type: 'invalid' });
      } else if (error.response && error.response.data.new_password) {
        setError('password', {
          message: error.response.data.new_password.pop(),
        });
      } else {
        toaster.showDefaultError(error);
      }
    }
  };

  if (!token) {
    return <Redirect to="/auth" />;
  }

  return (
    <AuthLayout>
      <AuthTitle
        title={intl.formatMessage({
          defaultMessage: 'Create a new password',
        })}
        sub={intl.formatMessage({
          defaultMessage: 'Get access to Sails with a new password',
        })}
      />
      <FormField
        label={intl.formatMessage({
          defaultMessage: 'New password',
          description: 'Form field label',
        })}
        marginBottom={majorScale(2)}
        className="medium"
      >
        <InputGroup
          placeholder={intl.formatMessage({
            defaultMessage: 'New password',
            description: 'Input placeholder',
          })}
          name="password"
          type="password"
          isInvalid={!!errors.password}
          validationMessage={errors.password && errors.password.message}
          ref={register({
            required: intl.formatMessage({
              defaultMessage: 'Please enter a new password to use',
              description: 'Input validation required',
            }),
          })}
        />
        <InputGroup
          placeholder={intl.formatMessage({
            defaultMessage: 'Retype password',
            description: 'Input placeholder',
          })}
          name="confirmPassword"
          type="password"
          isInvalid={!!errors.confirmPassword}
          validationMessage={
            errors.confirmPassword && errors.confirmPassword.message
          }
          ref={register({
            required: intl.formatMessage({
              defaultMessage: 'Please confirm your password',
              description: 'Input validation required',
            }),
            validate: (value: string) =>
              (watch('password') !== value &&
                intl.formatMessage({
                  defaultMessage: 'Passwords do not match',
                  description:
                    'Password confirm not equal to password, invalid',
                })) ||
              true,
          })}
        />
      </FormField>

      <Box display="flex" flexWrap="wrap" justifyContent="space-between">
        <Button
          primary
          width="100%"
          onClick={handleSubmit(onSubmit)}
          isLoading={formState.isSubmitting}
        >
          <FormattedMessage
            defaultMessage="Reset password"
            description="Primary button test - to reset password"
          />
        </Button>
      </Box>
    </AuthLayout>
  );
};
